import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { apiGet, apiPost, apiPut } from '../services/api';
import { useSettings } from './SettingsContext';

const KafkaContext = createContext();

export const useKafka = () => {
  const context = useContext(KafkaContext);
  if (!context) {
    throw new Error('useKafka must be used within a KafkaProvider');
  }
  return context;
};

export const KafkaProvider = ({ children }) => {
  const { kafkaSettings, isLoading: settingsLoading } = useSettings();
  const [settings, setSettings] = useState({
    // Connection settings
    kafka_servers: 'localhost:9092',
    client_id: 'datamolecule-client',
    connection_timeout: 10000,
    session_timeout: 30000,
    heartbeat_interval: 3000,
    max_poll_records: 500,
    
    // Producer settings
    producer_acks: 'all',
    producer_retries: 3,
    producer_batch_size: 16384,
    producer_linger_ms: 100,
    producer_compression_type: 'snappy',
    producer_max_request_size: 1048576,
    producer_buffer_memory: 33554432,
    
    // Consumer settings
    consumer_group_id: 'datamolecule-group',
    consumer_auto_offset_reset: 'latest',
    consumer_enable_auto_commit: true,
    consumer_auto_commit_interval: 1000,
    consumer_max_poll_interval: 300000,
    consumer_fetch_min_bytes: 1,
    consumer_fetch_max_wait: 500,
    
    // Topic settings
    default_partitions: 3,
    default_replication_factor: 1,
    default_retention_ms: 604800000, // 7 days
    default_cleanup_policy: 'delete',
    enable_topic_auto_creation: false,
    
    // Schema Registry settings
    schema_registry_url: 'http://localhost:8081',
    enable_schema_registry: false,
    schema_compatibility: 'BACKWARD',
    enable_auto_register_schemas: false,
    
    // Security settings
    security_protocol: 'PLAINTEXT',
    sasl_mechanism: 'PLAIN',
    sasl_username: '',
    sasl_password: '',
    ssl_keystore_location: '',
    ssl_keystore_password: '',
    ssl_truststore_location: '',
    ssl_truststore_password: '',
    
    // Monitoring settings
    enable_kafka_monitoring: true,
    enable_metrics_collection: true,
    metrics_sample_window_ms: 30000,
    metrics_num_samples: 2,
    enable_jmx_monitoring: false,
    jmx_port: 9999,
    
    // Performance settings
    enable_idempotence: true,
    max_in_flight_requests_per_connection: 5,
    request_timeout_ms: 30000,
    retry_backoff_ms: 100,
    reconnect_backoff_ms: 50,
    
    // Advanced settings
    enable_kafka_streams: false,
    streams_application_id: 'datamolecule-streams',
    streams_commit_interval_ms: 30000,
    streams_cache_max_bytes_buffering: 10485760,
    enable_exactly_once_semantics: false
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [kafkaStatus, setKafkaStatus] = useState('disconnected');
  const kafkaMonitoringRef = useRef(null);
  const [topics, setTopics] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [producers, setProducers] = useState([]);
  const [kafkaMetrics, setKafkaMetrics] = useState({
    brokers: 0,
    topics: 0,
    partitions: 0,
    totalMessages: 0,
    totalBytes: 0,
    throughputMsgPerSec: 0,
    throughputBytesPerSec: 0
  });

  // Load Kafka settings from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && kafkaSettings) {
      setSettings(prev => ({
        ...prev,
        ...kafkaSettings
      }));
      setIsLoading(false);
    }
  }, [kafkaSettings, settingsLoading]);

  // Load Kafka status and start monitoring
  useEffect(() => {
    if (!isLoading) {
      loadKafkaStatus();
      const cleanup = startMonitoring();
      return cleanup;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const loadKafkaStatus = async () => {
    try {
      // Load cluster health with authentication
      try {
        const health = await apiGet('kafka/cluster/health');
        setKafkaStatus(health.status || 'connected');
      } catch (error) {
        logger.warn('Could not load Kafka cluster health:', error);
        setKafkaStatus('disconnected');
      }
      
      // Load topics with authentication
      try {
        const topicsData = await apiGet('kafka/topics');
        setTopics(Array.isArray(topicsData) ? topicsData : (topicsData.data || []));
      } catch (error) {
        logger.warn('Could not load Kafka topics:', error);
        setTopics([]);
      }
      
      // Load consumers with authentication
      try {
        const consumersData = await apiGet('kafka/consumers');
        setConsumers(Array.isArray(consumersData) ? consumersData : (consumersData.data || []));
      } catch (error) {
        logger.warn('Could not load Kafka consumers:', error);
        setConsumers([]);
      }
      
      // Load producers with authentication
      try {
        const producersData = await apiGet('kafka/producers');
        setProducers(Array.isArray(producersData) ? producersData : (producersData.data || []));
      } catch (error) {
        logger.warn('Could not load Kafka producers:', error);
        setProducers([]);
      }
      
      // TODO: Load metrics (endpoint not implemented yet)
      // Temporarily disabled to prevent 404 errors
      // try {
      //   const metrics = await apiGet('kafka/metrics');
      //   setKafkaMetrics(metrics);
      // } catch (error) {
      //   logger.warn('Could not load Kafka metrics:', error);
      // }
    } catch (error) {
      logger.error('Error in loadKafkaStatus:', error);
      setKafkaStatus('disconnected');
    }
  };

  // Update Kafka settings
  const updateKafkaSettings = async (newSettings) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      
      // Save to backend
      await settingsService.updateSettingsBulk('kafka', updatedSettings);
      
      // Apply settings to Kafka if connected
      if (kafkaStatus === 'connected') {
        await applySettingsToKafka(newSettings);
      }
    } catch (error) {
      logger.error('Failed to save Kafka settings:', error);
      // Revert on error
      await loadKafkaSettings();
    }
  };

  // Apply settings to Kafka
  const applySettingsToKafka = async (newSettings) => {
    try {
      // Update producer configs
      if (Object.keys(newSettings).some(key => key.startsWith('producer_'))) {
        await apiPut('kafka/config/producers', {
          acks: newSettings.producer_acks || settings.producer_acks,
          retries: newSettings.producer_retries || settings.producer_retries,
          batchSize: newSettings.producer_batch_size || settings.producer_batch_size,
          lingerMs: newSettings.producer_linger_ms || settings.producer_linger_ms,
          compressionType: newSettings.producer_compression_type || settings.producer_compression_type
        });
      }
      
      // Update consumer configs
      if (Object.keys(newSettings).some(key => key.startsWith('consumer_'))) {
        await apiPut('kafka/config/consumers', {
          groupId: newSettings.consumer_group_id || settings.consumer_group_id,
          autoOffsetReset: newSettings.consumer_auto_offset_reset || settings.consumer_auto_offset_reset,
          enableAutoCommit: newSettings.consumer_enable_auto_commit ?? settings.consumer_enable_auto_commit,
          autoCommitInterval: newSettings.consumer_auto_commit_interval || settings.consumer_auto_commit_interval
        });
      }
    } catch (error) {
      logger.warn('Could not apply settings to Kafka:', error);
    }
  };

  // Get default topic configuration
  const getDefaultTopicConfig = useCallback(() => {
    return {
      partitions: settings.default_partitions,
      replicationFactor: settings.default_replication_factor,
      config: {
        'retention.ms': settings.default_retention_ms.toString(),
        'cleanup.policy': settings.default_cleanup_policy,
        'compression.type': settings.producer_compression_type,
        'max.message.bytes': settings.producer_max_request_size.toString()
      }
    };
  }, [settings]);

  // Create topic with default settings
  const createTopicWithDefaults = async (topicName, overrides = {}) => {
    const defaultConfig = getDefaultTopicConfig();
    const topicConfig = {
      name: topicName,
      ...defaultConfig,
      ...overrides
    };
    
    try {
      const response = await apiPost('kafka/topics', topicConfig);
      await loadKafkaStatus(); // Refresh topics
      return response;
    } catch (error) {
      logger.error('Failed to create topic:', error);
      throw error;
    }
  };

  // Get default producer configuration
  const getDefaultProducerConfig = useCallback(() => {
    return {
      acks: settings.producer_acks,
      retries: settings.producer_retries,
      batchSize: settings.producer_batch_size,
      lingerMs: settings.producer_linger_ms,
      compressionType: settings.producer_compression_type,
      maxRequestSize: settings.producer_max_request_size,
      bufferMemory: settings.producer_buffer_memory,
      enableIdempotence: settings.enable_idempotence,
      maxInFlightRequestsPerConnection: settings.max_in_flight_requests_per_connection
    };
  }, [settings]);

  // Get default consumer configuration
  const getDefaultConsumerConfig = useCallback(() => {
    return {
      groupId: settings.consumer_group_id,
      autoOffsetReset: settings.consumer_auto_offset_reset,
      enableAutoCommit: settings.consumer_enable_auto_commit,
      autoCommitInterval: settings.consumer_auto_commit_interval,
      maxPollInterval: settings.consumer_max_poll_interval,
      fetchMinBytes: settings.consumer_fetch_min_bytes,
      fetchMaxWait: settings.consumer_fetch_max_wait,
      maxPollRecords: settings.max_poll_records
    };
  }, [settings]);

  // Start monitoring
  const startMonitoring = useCallback(() => {
    if (!settings.enable_kafka_monitoring) return;

    // Clean up any existing interval
    if (kafkaMonitoringRef.current) {
      clearInterval(kafkaMonitoringRef.current);
    }

    const interval = setInterval(async () => {
      await loadKafkaStatus();
    }, 30000); // Every 30 seconds

    kafkaMonitoringRef.current = interval;

    return () => {
      clearInterval(interval);
      kafkaMonitoringRef.current = null;
    };
  }, [settings.enable_kafka_monitoring, loadKafkaStatus]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    if (kafkaMonitoringRef.current) {
      clearInterval(kafkaMonitoringRef.current);
      kafkaMonitoringRef.current = null;
    }
  }, []);

  // Get Kafka recommendations
  const getKafkaRecommendations = useCallback(() => {
    const recommendations = [];

    // Connection recommendations
    if (kafkaStatus === 'disconnected') {
      recommendations.push({
        type: 'error',
        category: 'connectivity',
        title: 'Kafka Connection Failed',
        description: 'Unable to connect to Kafka cluster. Check server configuration and network connectivity.',
        action: 'check_connectivity'
      });
    }

    // Topic recommendations
    if (topics.length > 50 && settings.default_retention_ms > 2592000000) { // > 30 days
      recommendations.push({
        type: 'warning',
        category: 'storage',
        title: 'High Retention Period',
        description: `${topics.length} topics with ${settings.default_retention_ms / 86400000} day retention. Consider reducing to save storage.`,
        action: 'reduce_retention'
      });
    }

    // Producer recommendations
    if (settings.producer_acks === '1' && settings.enable_idempotence) {
      recommendations.push({
        type: 'info',
        category: 'reliability',
        title: 'Improve Producer Reliability',
        description: 'Consider using acks=all with idempotence for better data durability.',
        action: 'improve_producer_reliability'
      });
    }

    // Consumer lag recommendations
    if (consumers.some(consumer => consumer.lag > 10000)) {
      recommendations.push({
        type: 'warning',
        category: 'performance',
        title: 'High Consumer Lag',
        description: 'Some consumers are lagging behind. Consider increasing consumer instances or optimizing processing.',
        action: 'optimize_consumers'
      });
    }

    // Partition recommendations
    if (topics.some(topic => topic.partitions === 1 && topic.messageRate > 1000)) {
      recommendations.push({
        type: 'info',
        category: 'scalability',
        title: 'Increase Topic Partitions',
        description: 'High-throughput topics with single partition may benefit from more partitions.',
        action: 'increase_partitions'
      });
    }

    return recommendations;
  }, [kafkaStatus, topics, settings, consumers]);

  // Apply recommendation
  const applyRecommendation = useCallback(async (action) => {
    switch (action) {
      case 'reduce_retention':
        await updateKafkaSettings({ default_retention_ms: 604800000 }); // 7 days
        break;
      case 'improve_producer_reliability':
        await updateKafkaSettings({ producer_acks: 'all', enable_idempotence: true });
        break;
      case 'increase_partitions':
        await updateKafkaSettings({ default_partitions: 6 });
        break;
      default:
        logger.warn('Unknown recommendation action:', action);
    }
  }, [updateKafkaSettings]);

  const value = {
    settings,
    isLoading,
    kafkaStatus,
    topics,
    consumers,
    producers,
    kafkaMetrics,
    recommendations: getKafkaRecommendations(),
    
    // Settings management
    updateKafkaSettings,
    
    // Default configurations
    getDefaultTopicConfig,
    getDefaultProducerConfig,
    getDefaultConsumerConfig,
    
    // Operations with defaults
    createTopicWithDefaults,
    
    // Monitoring
    startMonitoring,
    stopMonitoring,
    loadKafkaStatus,
    
    // Utilities
    applyRecommendation
  };

  return (
    <KafkaContext.Provider value={value}>
      {children}
    </KafkaContext.Provider>
  );
};

export default KafkaContext;